import { useState, useRef } from "react";
import { Check, Sparkles, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface Pack {
  id: string;
  name: string;
  videos: number;
  posters: number;
  price: number;
  popular?: boolean;
}

interface PremiumPackCardProps {
  pack: Pack;
  isSelected: boolean;
  onSelect: () => void;
  index: number;
}

export const PremiumPackCard = ({ pack, isSelected, onSelect, index }: PremiumPackCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showBurst, setShowBurst] = useState(false);
  const [tiltStyle, setTiltStyle] = useState({});
  const cardRef = useRef<HTMLButtonElement>(null);
  const { formatPrice } = useLanguage();

  const handleSelect = () => {
    setShowBurst(true);
    onSelect();
    setTimeout(() => setShowBurst(false), 600);
  };

  // 3D tilt effect on mouse move (desktop only)
  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!cardRef.current || window.innerWidth < 768) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;

    setTiltStyle({
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`,
      transition: 'transform 0.1s ease-out'
    });
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTiltStyle({
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
      transition: 'transform 0.3s ease-out'
    });
  };

  // Calculate savings percentage
  const basePrice = 75; // Starter price per video
  const pricePerVideo = pack.price / pack.videos;
  const savingsPercent = Math.round((1 - pricePerVideo / basePrice) * 100);

  return (
    <button
      ref={cardRef}
      onClick={handleSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ 
        animationDelay: `${index * 100}ms`,
        ...tiltStyle
      }}
      className={cn(
        "relative p-5 md:p-6 rounded-xl border-2 text-left w-full",
        "animate-card-slide-up will-change-transform",
        "active:scale-[0.97] touch-manipulation",
        "transition-colors duration-300",
        isSelected
          ? "border-neon-cyan bg-neon-cyan/10 shadow-[0_0_30px_rgba(0,212,255,0.3)]"
          : "border-border/40 bg-card/50 hover:border-neon-purple/60 hover:bg-card/70"
      )}
    >
      {/* Cursor glow effect (desktop) */}
      {isHovered && !isSelected && (
        <div 
          className="absolute inset-0 rounded-xl pointer-events-none hidden md:block"
          style={{
            background: 'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), hsl(var(--neon-purple) / 0.15) 0%, transparent 50%)'
          }}
        />
      )}

      {/* Selection burst particles */}
      {showBurst && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-neon-cyan animate-particle-burst"
              style={{
                left: '50%',
                top: '50%',
                '--angle': `${i * 30}deg`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}

      {/* Popular badge */}
      {pack.popular && (
        <div className={cn(
          "absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold",
          "bg-gradient-to-r from-neon-purple to-neon-pink",
          "flex items-center gap-1 whitespace-nowrap",
          "shadow-[0_0_15px_hsl(var(--neon-pink)/0.4)]",
          isSelected && "animate-pulse"
        )}>
          <Sparkles className="w-3 h-3" />
          Most Popular
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <h3 className="text-base md:text-lg font-bold">{pack.name}</h3>
          {savingsPercent > 0 && (
            <span className={cn(
              "px-2 py-0.5 rounded-full text-[10px] font-bold",
              "bg-neon-green/20 text-neon-green border border-neon-green/30",
              isSelected && "animate-bounce-subtle"
            )}>
              -{savingsPercent}%
            </span>
          )}
        </div>

        <div className="space-y-1 text-sm text-muted-foreground">
          <p className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-neon-cyan" />
            {pack.videos} Videos
          </p>
          <p className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-neon-pink" />
            {pack.posters} Posters
          </p>
        </div>

        <div className="pt-3 border-t border-border/40">
          <p className={cn(
            "text-2xl font-bold transition-all duration-300",
            isSelected ? "text-neon-cyan animate-price-glow" : "text-foreground"
          )}>
            {formatPrice(pack.price)}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatPrice(Math.round(pack.price / pack.videos))} per video
          </p>
        </div>
      </div>

      {/* Selection checkmark */}
      {isSelected && (
        <div className="absolute top-4 right-4 animate-check-pop">
          <div className="w-7 h-7 rounded-full bg-neon-cyan/20 flex items-center justify-center shadow-[0_0_15px_hsl(var(--neon-cyan)/0.4)]">
            <Check className="w-4 h-4 text-neon-cyan" />
          </div>
        </div>
      )}

      {/* Card shine effect on hover */}
      <div className={cn(
        "absolute inset-0 rounded-xl transition-opacity duration-500 pointer-events-none overflow-hidden",
        isHovered ? "opacity-100" : "opacity-0"
      )}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
      </div>
    </button>
  );
};
